import type { Feature, FeatureCollection } from 'geojson';

import {
    LEAK_ENTITY_STATUSES,
    POI_ENTITY_STATUSES,
    enabledLeakStatuses,
    enabledPoiStatuses,
    normaliseEntityType,
    type LeakEntityStatus,
    type PoiEntityStatus,
} from './entityStatus';
import { getDateRangeBounds } from './mapFilterHelpers';
import type { DateRangeOption, LeakVisibilityOption, ShowOption } from './mapFilters';

// The split-model Filters state and its map-side application. Each filter names which
// projected entities it applies to — never assumed to cover both just because they share
// a source row:
//
//   POI status toggles   → POI entities only.
//   Leak status toggles  → leak entities only (the families filter independently — a leak
//                          stays visible with its parent POI filtered out, and vice versa).
//   Show ('searched')    → POI entities only (a confirmed leak implies an assessment, so
//                          the restriction is a tautology for leaks — working default).
//   Date range           → both, via each entity's own backing-row dates (working default).
//   Leak Visibility      → leak entities only (a leak attribute; its old row-level effect
//                          on POIs is superseded — working default, an intentional change).
//   Pipe Type            → both entities of a row (shared physical context — one toggle
//                          hides both features of that row; accepted working default).
//
// Unknown status strings need no special rule here: the server derivation only emits the
// registry vocabulary, and anything unclassifiable arrives as the visible 'unclassified'
// status with its own toggle — the one uniform treatment that replaced the old pipeline's
// drop-vs-normalise split.

export type PoiStatusToggleState = Record<PoiEntityStatus, boolean>;
export type LeakStatusToggleState = Record<LeakEntityStatus, boolean>;

export interface EntityMapFilterState {
    show: ShowOption;
    dateRange: DateRangeOption;
    customDateRange: { from: string | null; to: string | null };
    leakVisibility: LeakVisibilityOption;
    poiStatuses: PoiStatusToggleState;
    leakStatuses: LeakStatusToggleState;
    pipeTypes: {
        main: boolean;
        communication: boolean;
        customerSupplyLine: boolean;
        stopTapBoundaryBox: boolean;
        fitting: boolean;
        unknown: boolean;
    };
}

// Defaults come from the whole registry: enabled statuses start visible, disabled
// statuses start (and stay) false — a disabled status renders nowhere, and that includes
// never passing a filter.
function registryDefaults<S extends string>(statuses: readonly { status: S; enabled: boolean }[]): Record<S, boolean> {
    return Object.fromEntries(statuses.map((d) => [d.status, d.enabled])) as Record<S, boolean>;
}

export const DEFAULT_ENTITY_MAP_FILTER_STATE: EntityMapFilterState = {
    show: 'all',
    dateRange: 'all',
    customDateRange: { from: null, to: null },
    leakVisibility: 'all',
    poiStatuses: registryDefaults(POI_ENTITY_STATUSES),
    leakStatuses: registryDefaults(LEAK_ENTITY_STATUSES),
    pipeTypes: {
        main: true,
        communication: true,
        customerSupplyLine: true,
        stopTapBoundaryBox: true,
        fitting: true,
        unknown: true,
    },
};

const PIPE_TYPE_VALUES: Record<keyof EntityMapFilterState['pipeTypes'], string> = {
    main: 'main',
    communication: 'comms',
    customerSupplyLine: 'cspl',
    stopTapBoundaryBox: 'stbb',
    fitting: 'fitting',
    unknown: 'unknown',
};

function isDateInRange(date: unknown, from: string | null, to: string | null): boolean {
    if (!date || typeof date !== 'string') return false;
    if (from !== null && date < from) return false;
    if (to !== null && date > to) return false;
    return true;
}

function passesDateFilter(feature: Feature, from: string | null, to: string | null): boolean {
    const searchDate: unknown = feature.properties?.['search_date'];
    const surveyedDate: unknown = feature.properties?.['surveyed_date'];
    return isDateInRange(searchDate, from, to) || isDateInRange(surveyedDate, from, to);
}

function passesPipeTypeFilter(feature: Feature, enabled: Set<string>): boolean {
    const pipeType: unknown = feature.properties?.['pipe_type'];
    // Null/missing passes whenever some toggles are on (the pre-split quirk, retained:
    // the PipeTypes enum has no Unknown member, so 'unknown' selects null pipe types).
    if (pipeType === null || pipeType === undefined || pipeType === '') return true;
    return typeof pipeType === 'string' && enabled.has(pipeType);
}

/**
 * Applies the committed split-model filters to an entity FeatureCollection, at source
 * level (cluster counts follow). Assumes toggles/entity gating has already run — this is
 * the Filters panel's contribution only.
 */
export function applyEntityFilters(
    data: FeatureCollection,
    filters: EntityMapFilterState,
    now?: Date,
): FeatureCollection {
    const { from, to } = getDateRangeBounds(
        { dateRange: filters.dateRange, customDateRange: filters.customDateRange },
        now,
    );
    const dateActive = !(from === null && to === null);
    const invalidRange = from !== null && to !== null && from > to;

    const enabledPipeValues = new Set(
        (Object.keys(filters.pipeTypes) as (keyof EntityMapFilterState['pipeTypes'])[])
            .filter((k) => filters.pipeTypes[k])
            .map((k) => PIPE_TYPE_VALUES[k]),
    );
    const anyPipeEnabled = enabledPipeValues.size > 0;

    const features = data.features.filter((feature) => {
        if (invalidRange) return false;

        const properties = feature.properties ?? {};
        const entityType = normaliseEntityType(properties['entity_type'] as string | null | undefined);
        const status = properties['status'] as string | undefined;

        if (entityType === 'poi') {
            if (!filters.poiStatuses[status as PoiEntityStatus]) return false;
            if (filters.show === 'searched') {
                const searchDate: unknown = properties['search_date'];
                if (!searchDate || typeof searchDate !== 'string') return false;
            }
        } else {
            if (!filters.leakStatuses[status as LeakEntityStatus]) return false;
            if (filters.leakVisibility !== 'all') {
                const visible: unknown = properties['is_leak_visible'];
                // Null/never-recorded is excluded by both non-All options.
                if (visible === null || visible === undefined) return false;
                if (Boolean(visible) !== (filters.leakVisibility === 'visible')) return false;
            }
        }

        if (dateActive && !passesDateFilter(feature, from, to)) return false;

        if (!anyPipeEnabled) return false;
        if (!passesPipeTypeFilter(feature, enabledPipeValues)) return false;

        return true;
    });

    return { type: 'FeatureCollection', features };
}

/**
 * The table/stats mirror of {@link applyEntityFilters}: the same committed state
 * serialised to the string entity-status query params (plus the shared dimensions).
 * Parity between this and the map-side application is what the shared-fixture tests hold.
 */
export interface EntityTableFilterParams {
    show?: string;
    dateFrom?: string;
    dateTo?: string;
    leakVisibility?: boolean;
    poiEntityStatusFilter?: boolean;
    poiEntityStatuses?: string[];
    leakEntityStatusFilter?: boolean;
    leakEntityStatuses?: string[];
    pipeTypeFilter?: boolean;
    pipeTypes?: number[];
    pipeTypeUnknown?: boolean;
}

const PIPE_TYPE_ENUM: Record<Exclude<keyof EntityMapFilterState['pipeTypes'], 'unknown'>, number> = {
    communication: 0,
    customerSupplyLine: 1,
    main: 2,
    fitting: 3,
    stopTapBoundaryBox: 4,
};

export function serialiseEntityFiltersToParams(filters: EntityMapFilterState, now?: Date): EntityTableFilterParams {
    const params: EntityTableFilterParams = {};

    if (filters.show === 'searched') params.show = 'searched';

    const { from, to } = getDateRangeBounds(
        { dateRange: filters.dateRange, customDateRange: filters.customDateRange },
        now,
    );
    if (from !== null) params.dateFrom = from;
    if (to !== null) params.dateTo = to;

    if (filters.leakVisibility !== 'all') params.leakVisibility = filters.leakVisibility === 'visible';

    const poiStatuses = enabledPoiStatuses()
        .map((d) => d.status)
        .filter((s) => filters.poiStatuses[s]);
    if (poiStatuses.length < enabledPoiStatuses().length) {
        params.poiEntityStatusFilter = true;
        if (poiStatuses.length > 0) params.poiEntityStatuses = poiStatuses;
    }

    const leakStatuses = enabledLeakStatuses()
        .map((d) => d.status)
        .filter((s) => filters.leakStatuses[s]);
    if (leakStatuses.length < enabledLeakStatuses().length) {
        params.leakEntityStatusFilter = true;
        if (leakStatuses.length > 0) params.leakEntityStatuses = leakStatuses;
    }

    const pipeKeys = Object.keys(filters.pipeTypes) as (keyof EntityMapFilterState['pipeTypes'])[];
    const anyPipeOff = pipeKeys.some((k) => !filters.pipeTypes[k]);
    if (anyPipeOff) {
        params.pipeTypeFilter = true;
        const enumValues = pipeKeys
            .filter((k): k is keyof typeof PIPE_TYPE_ENUM => k !== 'unknown' && filters.pipeTypes[k])
            .map((k) => PIPE_TYPE_ENUM[k]);
        if (enumValues.length > 0) params.pipeTypes = enumValues;
        if (filters.pipeTypes.unknown) params.pipeTypeUnknown = true;
    }

    return params;
}
